/**
 * Story 1.9 — Modal exibido quando o bundle JS local esta stale.
 *
 * Mostra mensagem amigavel + countdown de 5s antes de forcar reload.
 * Usuario pode clicar "Atualizar agora" para reload imediato.
 */
import { useEffect, useState } from 'react';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { forceReloadApp } from '../hooks/useVersionCheck';

interface VersionMismatchModalProps {
  serverVersion: string | null;
  clientVersion: string;
  /**
   * Quando true, modal renderiza. Quando false, retorna null.
   */
  visible: boolean;
  /**
   * Tempo (ms) ate auto-reload. Default 5000 (5s).
   * Usuario pode clicar "Atualizar agora" antes.
   */
  autoReloadAfterMs?: number;
}

export function VersionMismatchModal({
  serverVersion,
  clientVersion,
  visible,
  autoReloadAfterMs = 5000,
}: VersionMismatchModalProps) {
  const [secondsLeft, setSecondsLeft] = useState(
    Math.ceil(autoReloadAfterMs / 1000),
  );

  useEffect(() => {
    if (!visible) return;
    const tick = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tick);
          forceReloadApp();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="version-mismatch-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div className="flex-1">
            <h2
              id="version-mismatch-title"
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              Nova versão disponível
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Detectamos que sua tela está desatualizada. Vamos recarregar
              automaticamente para você ver a versão mais recente.
            </p>
          </div>
        </div>

        <div className="mb-4 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/50">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Sua versão: <span className="font-mono">{clientVersion}</span>
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Versão do servidor:{' '}
            <span className="font-mono">{serverVersion ?? 'desconhecida'}</span>
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Recarregando em <strong>{secondsLeft}s</strong>...
          </p>
          <button
            type="button"
            onClick={() => forceReloadApp()}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar agora
          </button>
        </div>
      </div>
    </div>
  );
}
